import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const { FiPlay, FiPause, FiCheck, FiHeadphones, FiMic, FiClock, FiDollarSign, FiShield, FiZap, FiBookOpen, FiArrowRight } = FiIcons;

/* Nine real GPU-narrated voices, sampled on our own domain (no vendor exposure). */
const VOICES = [
  { id: 'michael', name: 'Michael', persona: 'Warm audiobook baritone', best: 'Memoir · literary fiction' },
  { id: 'stuart_bell', name: 'Stuart', persona: 'Measured classic narrator', best: 'Long-form nonfiction' },
  { id: 'alba', name: 'Alba', persona: 'Bright, engaging read', best: 'Contemporary fiction' },
  { id: 'peter_yearsley', name: 'Peter', persona: 'Seasoned storyteller', best: 'Historical · epic' },
  { id: 'bill_boerst', name: 'Bill', persona: 'Steady, grounded voice', best: 'Thriller · mystery' },
  { id: 'george', name: 'George', persona: 'Deep, cinematic tone', best: 'Sci-fi · fantasy' },
  { id: 'jean', name: 'Jean', persona: 'Soft, intimate delivery', best: 'Romance · YA' },
  { id: 'charles', name: 'Charles', persona: 'Crisp, articulate read', best: 'Business · self-help' },
  { id: 'paul', name: 'Paul', persona: 'Relaxed, conversational', best: 'Guides · how-to' },
];

const AVATARS = ['from-emerald-500 to-teal-600', 'from-amber-500 to-orange-600', 'from-sky-500 to-indigo-600',
  'from-rose-500 to-pink-600', 'from-slate-600 to-slate-800', 'from-violet-500 to-purple-700',
  'from-cyan-500 to-blue-600', 'from-lime-500 to-emerald-600', 'from-stone-500 to-neutral-700'];

function VoiceCard({ v, index, playing, onToggle }) {
  const isPlaying = playing === v.id;
  return (
    <div className={`rounded-2xl border bg-white p-4 transition-shadow ${isPlaying ? 'border-emerald-400 shadow-lg' : 'border-stone-200 hover:shadow-md'}`}>
      <div className="flex items-center gap-3">
        <button onClick={() => onToggle(v.id)}
          className={`w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br ${AVATARS[index % AVATARS.length]} flex items-center justify-center text-white shadow`}>
          <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <div className="font-bold text-stone-900 text-sm">{v.name}</div>
          <div className="text-xs text-stone-500">{v.persona}</div>
        </div>
      </div>
      <div className="mt-3 text-[11px] font-medium text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1 inline-block">{v.best}</div>
    </div>
  );
}

export default function AudiobookAddonPage() {
  const audioRef = React.useRef(null);
  const [playing, setPlaying] = React.useState(null);
  React.useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const toggle = (id) => {
    const a = audioRef.current;
    if (!a) return;
    if (playing === id) { a.pause(); setPlaying(null); return; }
    a.src = `/voices/${id}.mp3`;
    a.play().then(() => setPlaying(id)).catch(() => setPlaying(null));
  };

  const included = [
    { icon: FiHeadphones, title: 'Narration is included', desc: 'Every ManuscriptSmith plan turns your finished chapters into audiobook narration — no separate audiobook subscription, no per-minute meter.' },
    { icon: FiMic, title: 'Nine studio voices', desc: 'Warm, characterful narrators matched to your genre — audition them right here, then narrate your whole book in the one you pick.' },
    { icon: FiClock, title: 'Chapters in minutes', desc: 'Press narrate and a chapter becomes clean audio in minutes. No booth, no editing suite, no engineer.' },
    { icon: FiZap, title: 'Listen while you edit', desc: 'Play a chapter inside the editor — the text follows along, and you can stop on any sentence to fix it, then re-narrate just that chapter.' },
    { icon: FiShield, title: 'Your voice, consistent', desc: 'One narrator carries every chapter at one level — the through-line human recordings lose across long books.' },
    { icon: FiDollarSign, title: 'Keep your earnings', desc: 'No royalty split, no thousands to a studio. The audiobook is yours, prepped toward retail from the same manuscript you wrote.' },
  ];

  const steps = [
    ['Write or import your book', 'Draft in the studio or import a finished manuscript — chapters, front matter and all.'],
    ['Pick a narrator', 'Audition the nine voices against your genre and choose the one that carries your story.'],
    ['Narrate & listen along', 'Generate chapter by chapter, listen in the editor, and fix anything before it ships.'],
    ['Prep for retail', 'Export clean audio and follow the guided path toward Audible via Amazon’s Virtual Voice on KDP.'],
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Audiobook Narration — Included with ManuscriptSmith"
        description="Turn your manuscript into a narrated audiobook without a studio. Nine studio-quality AI voices, listen-along editing, retail-ready export — included with every ManuscriptSmith plan."
        keywords="ai audiobook narration, included audiobook, turn book into audiobook, ai narrator, audiobook from manuscript"
        canonicalUrl="https://manuscriptsmith.com/audiobook"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-stone-900 via-emerald-950 to-stone-900 text-white">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url('/audiobook-hero.webp')" }} />
        <div className="relative max-w-5xl mx-auto px-4 py-24 text-center">
          <span className="inline-block rounded-full bg-emerald-500/20 border border-emerald-400/40 px-4 py-1 text-sm font-semibold text-emerald-200 mb-5">Included with every plan</span>
          <h1 className="text-4xl md:text-6xl font-bold mb-5 leading-tight">Your book, in a voice worth listening to</h1>
          <p className="text-xl text-stone-200 max-w-2xl mx-auto mb-8">
            ManuscriptSmith turns the chapters you write into a narrated audiobook — nine studio voices, listen-along
            editing, retail-ready audio. No booth, no engineer, no separate subscription.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href="#voices" className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-8 py-4 font-bold text-lg hover:bg-emerald-600 transition-colors">
              <SafeIcon icon={FiPlay} className="w-5 h-5" /> Hear the voices
            </a>
            <a href="https://app.manuscriptsmith.com/signup" className="inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/25 px-8 py-4 font-bold text-lg hover:bg-white/20 transition-colors">
              Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Included value grid */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-stone-900 mb-3">A whole audiobook studio, already in your plan</h2>
          <p className="text-stone-600 max-w-2xl mx-auto">Most authors pay a studio thousands, or juggle a second app. With ManuscriptSmith the narration lives beside your manuscript — same book, same login, no upsell.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {included.map((f) => (
            <div key={f.title} className="rounded-2xl border border-stone-200 p-6">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center mb-3">
                <SafeIcon icon={f.icon} className="w-5 h-5 text-emerald-700" />
              </div>
              <h3 className="font-bold text-stone-900 mb-1.5">{f.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Voice auditions */}
      <section id="voices" className="bg-stone-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-stone-900 mb-3">Audition your narrator</h2>
            <p className="text-stone-600 max-w-2xl mx-auto">Nine studio voices, each reading the same passage. Press play, find the one that sounds like your book — then narrate every chapter in it.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {VOICES.map((v, i) => <VoiceCard key={v.id} v={v} index={i} playing={playing} onToggle={toggle} />)}
          </div>
          <p className="text-center text-xs text-stone-400 mt-6">Real narration from the studio’s own voices. Your finished audiobook is rendered from the voice you choose.</p>
          <audio ref={audioRef} onEnded={() => setPlaying(null)} preload="none" />
        </div>
      </section>

      {/* Listen-along highlight */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="uppercase tracking-wide text-emerald-700 text-xs font-bold">Listen while you edit</span>
            <h2 className="text-3xl font-bold text-stone-900 mt-2 mb-3">Hear the read, fix it on the spot</h2>
            <p className="text-stone-600 leading-relaxed mb-4">
              Play a chapter inside the editor and the words follow the voice, sentence by sentence. Something reads
              wrong? Pause — that sentence stays selected, ready to rewrite in your voice — then re-narrate just that
              chapter. The tightest edit-listen loop in any writing tool.
            </p>
            <ul className="space-y-2">
              {['Text follows the narration line by line', 'Pause leaves the sentence ready to edit', 'Re-narrate one chapter, not the whole book', 'Stale-audio flag when text changes after narration'].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-stone-700"><SafeIcon icon={FiCheck} className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-gray-800 to-gray-900 p-5 shadow-2xl">
            <div className="rounded-lg bg-emerald-900/40 border border-emerald-500/30 px-3 py-2 mb-3 flex items-center gap-2 text-sm text-emerald-200">
              <SafeIcon icon={FiPlay} className="w-4 h-4" /> Listening — Chapter 3
            </div>
            <p className="text-[13px] leading-7 text-stone-200 font-serif" style={{ fontFamily: 'Georgia, serif' }}>
              The lighthouse keeper counted the ships the way other people counted their blessings —
              <span className="bg-emerald-500/30 rounded px-0.5"> slowly, and out loud.</span> That autumn,
              for the first time in forty years, one of them counted back.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-stone-50 py-16">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-stone-900 text-center mb-10">Manuscript to audiobook, in four steps</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map(([t, d], i) => (
              <div key={t} className="rounded-2xl bg-white border border-stone-200 p-5">
                <div className="w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center mb-3">{i + 1}</div>
                <h3 className="font-bold text-stone-900 mb-1.5 text-sm">{t}</h3>
                <p className="text-xs text-stone-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACX / retail honesty band */}
      <section className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-stone-900 mb-3">Ready for Audible — the honest way</h2>
        <p className="text-stone-600 max-w-2xl mx-auto mb-6">
          AI-narrated audio reaches Audible via Amazon’s Virtual Voice on KDP — we prep your book for that path and
          won’t overpromise a shortcut. Prefer a human narrator instead? Your prepped scripts, pronunciation guide and
          voice samples hand off cleanly to one.
        </p>
        <Link to="/audiobook/acx" className="inline-flex items-center gap-2 rounded-xl border border-stone-300 px-6 py-3 font-semibold text-stone-700 hover:bg-stone-50">
          See the retail path <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
        </Link>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-emerald-700 to-teal-800 text-white py-20 px-4 text-center">
        <SafeIcon icon={FiBookOpen} className="w-10 h-10 mx-auto mb-4 opacity-80" />
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Your audiobook is already part of the plan</h2>
        <p className="text-emerald-100 max-w-2xl mx-auto mb-8">Write the book, pick the voice, press narrate. Get started and hear your first chapter today.</p>
        <a href="https://app.manuscriptsmith.com/signup" className="inline-block rounded-xl bg-white text-emerald-800 font-bold px-8 py-4 text-lg hover:bg-emerald-50 transition-colors">Get started</a>
      </section>
    </div>
  );
}
